import {
  Button,
  CrossInCircleIcon,
  SearchIcon,
  useDebounce,
  IconButton,
  BareButton,
} from "@setel/web-ui";
import React, { useCallback, useRef, useState } from "react";
import { ArticleList } from "./article-list";
import {
  HelpCentrePath,
  HELP_CENTRE_SEARCH_DEBOUNCE_DELAY,
} from "~/lib/types/searchHistory";
import { Input } from "~/lib/components/layout/Form/Input/Input";
import { useSearchZendeskArticles } from "~/services/queries/zendesk.queries";
import { useHelpCentreHistory } from "../../utils/hooks/useHistory";
import { SearchHistoryList } from "./search-history-list";
import { LocaleLink, useLocaleNavigate } from "~/lib/components/link";
import { useTranslation } from "~/lib/utils/store/useTranslation";
import { trackUserEvent } from "../../utils/analytics";
import { EVENT_CHANNELS, EVENT_TYPES } from "../../utils/analytics.events";
import { MostSearchArticleList } from "./most-search-article-list";
import { useGetMostArticles } from "~/services/queries/api.zendesk.queries";
import { MOST_ARTICLES_LIMIT } from "~/lib/utils/const";
import csImgUrl from "./customer-support.svg";

export const WebSearchBar = ({
  isShowOnHero = true,
}: {
  isShowOnHero?: boolean;
}) => {
  const navigate = useLocaleNavigate();
  const { t, i18n } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const { data: mostSearchHistories, isLoading: isLoadingArticles } =
    useGetMostArticles({ limit: MOST_ARTICLES_LIMIT, locale: i18n.language });
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [searchHistories, addSearchHistory, removeSearchHistories] =
    useHelpCentreHistory();
  const debouncedSearchInput = useDebounce(
    searchInput,
    HELP_CENTRE_SEARCH_DEBOUNCE_DELAY
  );

  const navigateToSearchPage = useCallback(() => {
    addSearchHistory({ searchedInput: searchInput });
    navigate(`${HelpCentrePath.SEARCH_ARTICLE}?query=${searchInput || ""}`);
  }, [searchInput]);

  const { data: searchData } = useSearchZendeskArticles(
    {
      query: debouncedSearchInput,
      locale: i18n.language,
      per_page: 5,
    },
    {
      onSuccess: () => {
        if (!isDropdownVisible) {
          setDropdownVisible(true);
        }
      },
    }
  );

  return (
    <>
      {/* to allow width match with the content */}
      <div
        className={`${
          isShowOnHero ? "w-full relative" : "w-[811px] relative mx-[50px]"
        }`}
      >
        {isShowOnHero && (
          <div
            className={`h-full hide-in-mobile absolute right-16 bottom-[125px]`}
          >
            <img
              src={csImgUrl}
              className="block w-full h-[168px]"
              width={158}
              height={121}
              alt="customer support"
            />
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!searchInput) {
              return;
            }

            navigateToSearchPage();
          }}
          onReset={() => {
            setSearchInput("");
          }}
        >
          <Input
            className="flex items-center"
            inputClass={`${
              isShowOnHero ? "h-[80px]" : "h-12"
            } rounded-xl text-lg ${
              isDropdownVisible ? "rounded-b-none" : "shadow-bottom"
            } placeholder-gray-light`}
            placeholder={t("searchQuestions")}
            onFocus={() => {
              if (
                !searchInput &&
                searchHistories &&
                searchHistories?.length !== 0
              ) {
                setDropdownVisible(true);
                return;
              }

              if (
                searchData &&
                searchData?.results.length !== 0 &&
                !isDropdownVisible
              ) {
                setDropdownVisible(true);
              }
            }}
            onBlur={() => {
              setDropdownVisible(false);
            }}
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
            }}
            onClick={() => setDropdownVisible(true)}
            ref={inputRef}
            before={
              <IconButton
                data-testid="web-focus-input-button"
                onClick={() => {
                  inputRef.current?.focus();
                }}
              >
                <SearchIcon />
              </IconButton>
            }
            onKeyDown={() => {
              /** TODO: navigate thru dropdown using arrow key */
            }}
            after={
              <>
                {searchInput && (
                  <IconButton
                    className="mr-7"
                    type="reset"
                    data-testid="web-remove-input-button"
                  >
                    <CrossInCircleIcon className="text-lightgrey" />{" "}
                  </IconButton>
                )}
                {isShowOnHero && (
                  <Button
                    type="submit"
                    variant="primary"
                    size="small"
                    className="py-[27px] px-6 text-sm"
                    onClick={(e) => {
                      trackUserEvent(
                        EVENT_TYPES.WEB_SUPPORT_HELP_SEARCH_BAR_INPUT,
                        { keyword: searchInput },
                        [EVENT_CHANNELS.MIXPANEL]
                      );
                    }}
                  >
                    {t("search")?.toUpperCase()}
                  </Button>
                )}
              </>
            }
          />
        </form>

        <div
          className={`absolute w-full bg-white z-[75] dropdown-search rounded-b-xl  ${
            isDropdownVisible ? "block" : "hidden"
          }`}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
        >
          {!debouncedSearchInput &&
            searchHistories &&
            searchHistories?.length !== 0 && (
              <>
                <div className="flex m-6 mb-0">
                  <p className="font-semibold text-xl">{t("searchHistory")}</p>
                  <BareButton
                    className="text-[15px] text-brand-500 ml-8"
                    onClick={() => {
                      removeSearchHistories();
                    }}
                  >
                    {t("clear")}
                  </BareButton>
                </div>
                <SearchHistoryList
                  variant="web"
                  onClick={addSearchHistory}
                  items={searchHistories}
                />
              </>
            )}
          {!debouncedSearchInput && mostSearchHistories && (
            <div className="pb-6">
              <div className="flex m-6 mb-0">
                <p className="font-semibold text-xl">{t("mostSearch")}</p>
              </div>
              <MostSearchArticleList
                variant="web"
                items={mostSearchHistories}
              />
            </div>
          )}
          {searchData && (
            <>
              <ArticleList
                articles={searchData.results}
                searchedInput={debouncedSearchInput}
                addSearchHistory={addSearchHistory}
                locale={i18n.language.toLowerCase()}
              />

              <LocaleLink
                to={`${HelpCentrePath.SEARCH_ARTICLE}${
                  searchInput && `?query=${searchInput}`
                }`}
                className="text-brand-500 flex px-8 py-6 hover:bg-brand-50 rounded-b-[8px]"
                onClick={() => {
                  addSearchHistory({ searchedInput: searchInput });
                }}
              >
                <SearchIcon className="w-6 h-6 mr-2 my-auto" />
                <span>
                  {t("viewMoreResult", {
                    searchInput: debouncedSearchInput,
                  })}
                </span>
              </LocaleLink>
            </>
          )}
        </div>
      </div>
    </>
  );
};
